import {
  CASE_SCHEMA_VERSION,
  migrateCaseState,
  resolveCaseDesign,
} from 'case-designer/specs';

// Item-fit (CONFIG-08, D-08): the single tunable clearance boundary (inches). Green
// requires ≥ CLEARANCE_IN on every axis; below it (but still fitting) is amber; a
// negative clearance on any axis is red. The engine (case-designer-engine.js) carries
// its own CLEARANCE_IN with the identical value so the ghost box color matches this
// verdict — change both together if the boundary ever moves.
const CLEARANCE_IN = 1;
// The three fixed shipped verdict hex (UI-SPEC). Do NOT introduce #22c55e.
const FIT_HEX = { green: '#16a34a', amber: '#f59e0b', red: '#ef4444' };
// localStorage keys for the Slice 2 save/autosave layer (SHARE-02, D-11). Namespaced +
// versioned: current writes use v2 and reads migrate v1 rather than dropping customer
// designs. `saves` holds up to 20 named designs
// (D-04); `autosave` holds the single last-session snapshot (D-07).
const LS_SAVES_KEY = 'caselabs.caseDesigner.saves';
const LS_AUTOSAVE_KEY = 'caselabs.caseDesigner.autosave';
// Hard cap on stored named designs (D-04). Enforced on write (block, no overwrite) AND
// on read (truncate, defensive against tampering — T-04-10).
const SAVES_MAX = 20;

class CaseDesigner extends HTMLElement {
  connectedCallback() {
    const dataEl = document.getElementById('case-designer-data');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this.state = { ...data.defaults };
    if (!Array.isArray(this.state.options)) this.state.options = [];
    // Partitions (CONFIG-07): up to 12 numeric rows per shell, split Base/Lid
    // (D-06). Each row is { dir: 'width'|'depth'|'shelf', pos: <inches> }. Defaults
    // empty; the engine renders each as a live thin wall (makePartitionWall).
    if (!this.state.partitions || typeof this.state.partitions !== 'object') {
      this.state.partitions = { base: [], lid: [] };
    }
    if (!Array.isArray(this.state.partitions.base)) this.state.partitions.base = [];
    if (!Array.isArray(this.state.partitions.lid)) this.state.partitions.lid = [];
    // Item-fit (CONFIG-08, D-08/D-09): opt-in "will my item fit?" check. Off by
    // default so no verdict/ghost renders until the customer turns it on. w/d/h are
    // the customer's item dimensions (inches); desc is an optional free-text label.
    // The single CLEARANCE_IN constant (below) drives the green/amber/red boundary in
    // BOTH this sidebar verdict and the engine ghost so they never disagree.
    if (!this.state.itemFit || typeof this.state.itemFit !== 'object') {
      this.state.itemFit = { on: false, w: null, d: null, h: null, desc: '' };
    }

    // Which partition tab is active in the sidebar (view state, not persisted).
    this._partitionTab = 'base';
    // Max partitions per tab (UI-SPEC 12-cap).
    this.PARTITION_MAX = 12;
    // Pristine copy of the shipped defaults — the deep-link encoder writes only
    // non-default values against this (state is mutated in place).
    this._defaults = { ...data.defaults };
    this.limits = data.limits || { minDim: 4, maxDim: 96 };
    this.catalogs = data.catalogs || {};
    this.pricing = data.pricing || {};
    this.contactEmail = data.contactEmail || '';
    this.estimateNodes = this.querySelectorAll('[data-estimate]');

    this.stage = this.querySelector('.cased__stage');
    this.canvas = this.querySelector('.cased__canvas');
    this.sidebar = this.querySelector('.cased__sidebar');
    this.note = this.querySelector('.cased__note');
    this.dimHelp = this.querySelector('[data-dimhelp]');
    this.designSummaries = this.querySelectorAll('[data-design-summary]');
    this.quoteForm = this.querySelector('.cased__quote-form');
    this.snapshotBtn = this.querySelector('[data-snapshot]');
    // Floating stage toolbar (ENGINE-06, Plan 05) — revealed once the engine boots.
    this.toolbar = this.querySelector('[data-toolbar]');
    // Live toolbar toggle state (auto-rotate + lights default ON; x-ray + measure OFF).
    this._toolState = { autorotate: true, xray: false, measure: false, lights: true };
    this.quoteCtas = this.querySelectorAll('[data-quote-cta]');
    this.otherNotesField = this.querySelector('[data-other-notes]');
    this.otherNotesInput = this.querySelector('[data-other-notes-input]');
    // Item-fit (CONFIG-08) DOM refs.
    this.itemFitToggle = this.querySelector('[data-item-fit-toggle]');
    this.itemFitFields = this.querySelector('[data-item-fit-fields]');
    this.itemFitInputs = this.querySelectorAll('[data-item-fit]');
    this.itemFitVerdict = this.querySelector('[data-item-fit-verdict]');
    this.itemFitClearance = this.querySelector('[data-item-fit-clearance]');
    // The quote step is the <details> that wraps the contact form.
    this.quoteStep = this.quoteForm ? this.quoteForm.closest('details.cased__step') : null;
    // The Partitions step drives X-ray auto-engage (D-07).
    this.partitionsStep = this.querySelector('[data-partitions-step]');
    // Fold-out table (frontlid only): the step is shown only for the front-lid
    // style, and choosing a side IS the preview -- see _syncTablePreview.
    this.tableStep = this.querySelector('[data-table-step]');
    this.tableMount = this.querySelector('[data-mount="table"]');

    // Taste base.css has `div:empty { display: none }` which would hide an empty
    // stage. Force a child and inline display so the rule can never match.
    if (this.stage && !this.stage.firstChild) {
      const filler = document.createElement('span');
      filler.setAttribute('aria-hidden', 'true');
      this.stage.appendChild(filler);
    }
    if (this.stage) {
      this.stage.style.setProperty('display', 'block', 'important');
    }

    this._boundInputs = [];
    this._onDimChange = this._handleDimChange.bind(this);
    this.querySelectorAll('input[data-dim]').forEach((input) => {
      input.addEventListener('input', this._onDimChange);
      this._boundInputs.push(input);
    });

    this._onSidebarChange = this._handleSidebarChange.bind(this);
    if (this.sidebar) this.sidebar.addEventListener('change', this._onSidebarChange);

    // Partition controls are delegated on the sidebar (rows are rebuilt on every
    // change, so per-node listeners would leak). change → direction radios +
    // tab buttons; input → position numbers; click → tabs / add / remove.
    this._onPartitionChange = this._handlePartitionChange.bind(this);
    this._onPartitionInput = this._handlePartitionInput.bind(this);
    this._onPartitionClick = this._handlePartitionClick.bind(this);
    if (this.sidebar) {
      this.sidebar.addEventListener('change', this._onPartitionChange);
      this.sidebar.addEventListener('input', this._onPartitionInput);
      this.sidebar.addEventListener('click', this._onPartitionClick);
    }

    // Enhanced stay-on-design quote submit (additive over the native POST fallback).
    this._onQuoteSubmit = this._handleQuoteSubmit.bind(this);
    if (this.quoteForm) this.quoteForm.addEventListener('submit', this._onQuoteSubmit);

    // CTA(s) open + scroll to the quote step.
    this._onQuoteCta = this._activateQuoteCta.bind(this);
    if (this.quoteCtas) {
      this.quoteCtas.forEach((btn) => btn.addEventListener('click', this._onQuoteCta));
    }

    // Snapshot download.
    this._onSnapshot = this._downloadSnapshot.bind(this);
    if (this.snapshotBtn) this.snapshotBtn.addEventListener('click', this._onSnapshot);

    // Share & Save (D-01/D-02): the copy-link button + the single reusable toast.
    // toastEl is queried here (before the render block) so boot toasts (D-10) resolve.
    this.shareCopyBtn = this.querySelector('[data-share-copy]');
    this.toastEl = this.querySelector('[data-toast]');
    this._onShareCopy = this._copyDesignLink.bind(this);
    if (this.shareCopyBtn) this.shareCopyBtn.addEventListener('click', this._onShareCopy);

    // Save current design + saved-designs list (SHARE-02). The Save button appends an
    // auto-named snapshot (cap 20); the list's Load/Delete actions are delegated on the
    // saves mount (rows are rebuilt on every change, so per-node listeners would leak).
    this.shareSaveBtn = this.querySelector('[data-share-save]');
    this.savesMount = this.querySelector('[data-mount="saves"]');
    this._onShareSave = this._saveDesign.bind(this);
    if (this.shareSaveBtn) this.shareSaveBtn.addEventListener('click', this._onShareSave);
    this._onSavesClick = this._handleSavesClick.bind(this);
    if (this.savesMount) this.savesMount.addEventListener('click', this._onSavesClick);

    // Stage toolbar (ENGINE-06): delegated click → engine methods. Buttons only
    // affect the 3D, so they are safely no-ops (guarded) until the engine boots.
    this._onToolbarClick = this._handleToolbarClick.bind(this);
    if (this.toolbar) this.toolbar.addEventListener('click', this._onToolbarClick);

    // "Other" style free-text notes (D-02) — input event so the quote spec stays current.
    this._onOtherNotes = this._handleOtherNotes.bind(this);
    if (this.otherNotesInput) this.otherNotesInput.addEventListener('input', this._onOtherNotes);

    // Item-fit (CONFIG-08): toggle reveals/hides the item inputs + verdict; W/D/H/desc
    // inputs recompute the verdict live. Works fully with the canvas dead.
    this._onItemFitToggle = this._handleItemFitToggle.bind(this);
    if (this.itemFitToggle) this.itemFitToggle.addEventListener('change', this._onItemFitToggle);
    this._onItemFitInput = this._handleItemFitInput.bind(this);
    if (this.itemFitInputs) {
      this.itemFitInputs.forEach((input) => input.addEventListener('input', this._onItemFitInput));
    }

    // Expanding the Partitions step auto-engages X-ray so dividers read through the
    // shell; collapsing restores the exact prior X-ray state (D-07, Pitfall 4).
    this._onPartitionsToggle = this._handlePartitionsToggle.bind(this);
    if (this.partitionsStep) this.partitionsStep.addEventListener('toggle', this._onPartitionsToggle);

    // If the native POST reloaded to the success state (JS-dead fallback path just
    // re-enabled by JS), mark the sent flag so the reset-on-change guard engages.
    if (this.quoteForm && this.quoteForm.querySelector('.cased__quote-success.is-visible')) {
      this._quoteSent = true;
    }

    // Load-time precedence (D-09): a deep link in location.search wins and overlays
    // its non-default values onto this.state BEFORE the first render (Slice 2 adds the
    // autosave branch here). Decode-only — never touches the DOM — so the single shared
    // _applyStateToDom() path below re-hydrates every control from the resolved state.
    this._resolveLoadPrecedence();

    // The ONE shared state->DOM re-hydration path — used at boot (defaults or deep link)
    // and, in Slice 2, by load-saved / autosave-restore. Regenerates all catalog controls
    // + pushes the Liquid-static inputs + guarded engine tail.
    this._applyStateToDom();

    // Flush any boot toast queued by the precedence resolver (D-10 reset notice). The
    // toast element is authored in the section; _showToast no-ops if it is absent.
    if (this._pendingToast) {
      this._showToast(this._pendingToast.text, this._pendingToast.tone);
      this._pendingToast = null;
    }

    // Saved-designs list boot state (SHARE-02). When localStorage is unavailable
    // (private/hardened mode) disable Save and show a muted note in the list mount —
    // copy-link + deep-link decode stay fully functional (Pitfall 7 / T-04-08).
    // Otherwise render the stored saves once (after the first render, D-07 order).
    if (!this._lsAvailable()) {
      if (this.shareSaveBtn) this.shareSaveBtn.disabled = true;
      if (this.savesMount) {
        const note = document.createElement('p');
        note.className = 'cased__caption cased__saves-unavailable';
        note.textContent =
          "Saving isn't available in this browser mode — you can still copy your design link.";
        this.savesMount.replaceChildren(note);
      }
    } else {
      this._renderSaves();
    }

    this._rebuild = debounce(() => {
      if (this.engine) {
        this._syncEngineLabels();
        this.engine.updateConfig(this.state);
      }
    }, 50);

    // Debounced live address-bar sync (D-03). Mirrors the _rebuild 50ms cadence. Every
    // user-driven mutation tail calls this._persist(); it is NEVER called at boot (D-08).
    // replaceState (NOT pushState) so Back does not walk every edit. try/catch so a
    // History-API throw never surfaces (T-04-06). Slice 2 extends this body to also
    // write the localStorage autosave on the same debounced path (D-11).
    this._persist = debounce(() => {
      try {
        history.replaceState(null, '', this._serializeDeepLink());
      } catch (e) {}
      // Slice 2 (D-11): the URL and the localStorage autosave move in lockstep on the
      // one debounced chokepoint. _persist is only ever called from a user-edit tail
      // (never at boot), so on a pure deep-link visit with no edits the prior autosave
      // is provably untouched (D-08 / A2 reading). _writeAutosave self-guards on storage
      // availability and swallows any throw, so it never breaks the URL sync above.
      this._writeAutosave();
    }, 50);

    this._observeStageForLazyBoot();
  }

  disconnectedCallback() {
    if (this._boundInputs) {
      this._boundInputs.forEach((input) => input.removeEventListener('input', this._onDimChange));
    }
    if (this.sidebar && this._onSidebarChange) {
      this.sidebar.removeEventListener('change', this._onSidebarChange);
    }
    if (this.sidebar && this._onPartitionChange) {
      this.sidebar.removeEventListener('change', this._onPartitionChange);
      this.sidebar.removeEventListener('input', this._onPartitionInput);
      this.sidebar.removeEventListener('click', this._onPartitionClick);
    }
    if (this.quoteForm && this._onQuoteSubmit) {
      this.quoteForm.removeEventListener('submit', this._onQuoteSubmit);
    }
    if (this.quoteCtas && this._onQuoteCta) {
      this.quoteCtas.forEach((btn) => btn.removeEventListener('click', this._onQuoteCta));
    }
    if (this.snapshotBtn && this._onSnapshot) {
      this.snapshotBtn.removeEventListener('click', this._onSnapshot);
    }
    if (this.shareCopyBtn && this._onShareCopy) {
      this.shareCopyBtn.removeEventListener('click', this._onShareCopy);
    }
    if (this.shareSaveBtn && this._onShareSave) {
      this.shareSaveBtn.removeEventListener('click', this._onShareSave);
    }
    if (this.savesMount && this._onSavesClick) {
      this.savesMount.removeEventListener('click', this._onSavesClick);
    }
    if (this.toolbar && this._onToolbarClick) {
      this.toolbar.removeEventListener('click', this._onToolbarClick);
    }
    if (this.otherNotesInput && this._onOtherNotes) {
      this.otherNotesInput.removeEventListener('input', this._onOtherNotes);
    }
    if (this.partitionsStep && this._onPartitionsToggle) {
      this.partitionsStep.removeEventListener('toggle', this._onPartitionsToggle);
    }
    if (this.itemFitToggle && this._onItemFitToggle) {
      this.itemFitToggle.removeEventListener('change', this._onItemFitToggle);
    }
    if (this.itemFitInputs && this._onItemFitInput) {
      this.itemFitInputs.forEach((input) => input.removeEventListener('input', this._onItemFitInput));
    }
    if (this.engine) this.engine.dispose();
    if (this._io) this._io.disconnect();
  }

  _renderSidebar() {
    this._renderCards('styles', this.querySelector('[data-mount="styles"]'), 'style', 'card');
    this._renderPills(this.querySelector('[data-mount="dimTypes"]'));
    this._renderCards('plywoods', this.querySelector('[data-mount="plywoods"]'), 'plywood', 'card');
    this._renderCards('finishes', this.querySelector('[data-mount="finishes"]'), 'finish', 'card');
    this._renderColors();
    this._renderCards('linings', this.querySelector('[data-mount="linings"]'), 'lining', 'swatch');
    this._renderCards('wheels', this.querySelector('[data-mount="wheels"]'), 'wheels', 'card');
    this._renderCards('tables', this.tableMount, 'table', 'card');
    this._renderOptions();
    this._renderPartitions();
    this._syncTableStep();
    this._updateDimHelp();
  }

  _renderCards(catalogKey, mountEl, group, kind) {
    if (!mountEl) return;
    const list = this.catalogs[catalogKey] || [];
    const currentId = this.state[group];
    // One shared radio name per group so the browser manages exclusivity.
    // Per-input unique names leave stale radios checked, which swallows the
    // change event when re-selecting a previously chosen option.
    const groupName = group + '-' + this._uid();
    const cards = list.map((item) => {
      const label = document.createElement('label');
      label.className = 'cased__card';
      if (kind === 'swatch') label.classList.add('cased__card--swatch');
      if (item.id === currentId) label.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'cased__card-input';
      input.name = groupName;
      input.dataset.group = group;
      input.dataset.id = item.id;
      input.checked = item.id === currentId;
      if (item.locked) input.dataset.locked = 'true';

      if (kind === 'swatch' && item.hex) {
        const swatch = document.createElement('span');
        swatch.className = 'swatch swatch--square cased__card-swatch';
        swatch.style.setProperty('--swatch--background', item.hex);
        label.appendChild(input);
        label.appendChild(swatch);
      } else {
        // Style cards get their server-rendered inline-SVG icon (cloned from the
        // author-controlled <template>, never innerHTML on catalog data — V5).
        const styleIcon = group === 'style' ? this._styleIconFor(item.icon) : null;
        if (styleIcon) {
          styleIcon.setAttribute('aria-hidden', 'true');
          label.appendChild(input);
          label.appendChild(styleIcon);
        } else {
          const chip = document.createElement('span');
          chip.className = 'cased__card-chip';
          chip.setAttribute('aria-hidden', 'true');
          label.appendChild(input);
          label.appendChild(chip);
        }
      }

      const text = document.createElement('span');
      text.className = 'cased__card-label';
      text.textContent = item.label;
      label.appendChild(text);
      return label;
    });
    mountEl.replaceChildren(...cards);
  }

  // Clone the server-rendered inline-SVG icon for a style id out of the hidden
  // <template data-style-icons>. Returns a detached node (or null) — the SVG is
  // author-controlled markup baked at render time, so cloning the trusted DOM
  // node avoids any innerHTML-on-catalog-string surface (V5 / T-03-01).
  _styleIconFor(iconId) {
    if (!iconId) return null;
    if (this._styleIconTemplate === undefined) {
      const tpl = this.querySelector('template[data-style-icons]');
      this._styleIconTemplate = tpl ? tpl.content : null;
    }
    if (!this._styleIconTemplate) return null;
    const source = this._styleIconTemplate.querySelector(
      '[data-style-icon="' + iconId + '"]'
    );
    return source ? source.cloneNode(true) : null;
  }

  _renderColors() {
    const mountEl = this.querySelector('[data-mount="colors"]');
    if (!mountEl) return;
    const finish = this.state.finish;
    const visible = (this.catalogs.colors || []).filter(
      (c) => Array.isArray(c.finishes) && c.finishes.includes(finish)
    );
    const groupName = 'color-' + this._uid();
    const cards = visible.map((c) => {
      const label = document.createElement('label');
      label.className = 'cased__swatch';
      if (c.id === this.state.color) label.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'cased__swatch-input';
      input.name = groupName;
      input.dataset.group = 'color';
      input.dataset.id = c.id;
      input.checked = c.id === this.state.color;

      const chip = document.createElement('span');
      chip.className = 'swatch swatch--square cased__swatch-chip';
      chip.style.setProperty('--swatch--background', c.hex);

      const text = document.createElement('span');
      text.className = 'cased__swatch-label';
      text.textContent = c.label;

      label.appendChild(input);
      label.appendChild(chip);
      label.appendChild(text);
      return label;
    });
    mountEl.replaceChildren(...cards);
  }

  _renderPills(mountEl) {
    if (!mountEl) return;
    const list = this.catalogs.dimTypes || [];
    const groupName = 'dimType-' + this._uid();
    const pills = list.map((item) => {
      const label = document.createElement('label');
      label.className = 'cased__pill';
      if (item.id === this.state.dimType) label.classList.add('is-selected');

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'cased__pill-input';
      input.name = groupName;
      input.dataset.group = 'dimType';
      input.dataset.id = item.id;
      input.checked = item.id === this.state.dimType;

      const text = document.createElement('span');
      text.className = 'cased__pill-label';
      text.textContent = item.label;

      label.appendChild(input);
      label.appendChild(text);
      return label;
    });
    mountEl.replaceChildren(...pills);
  }

  _renderOptions() {
    const mountEl = this.querySelector('[data-mount="options"]');
    if (!mountEl) return;
    const list = this.catalogs.options || [];
    const rows = list.map((item) => {
      const label = document.createElement('label');
      label.className = 'cased__check';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'cased__check-input';
      input.dataset.group = 'options';
      input.dataset.id = item.id;
      input.checked = this.state.options.indexOf(item.id) !== -1;

      const text = document.createElement('span');
      text.className = 'cased__check-label';
      text.textContent = item.label;

      label.appendChild(input);
      label.appendChild(text);
      return label;
    });
    mountEl.replaceChildren(...rows);
  }

  // ---- Partitions (CONFIG-07) ------------------------------------------
  // Numeric-only, no drag (D-06). Base/Lid tabs; up to 12 rows per tab. Each row =
  // a direction control (Width/Depth/Shelf) + a Position (in) numeric input + an
  // icon-only Remove button. All text via textContent/input.value (V5 / T-03-04).
  // Works fully with the canvas dead — no engine gating.

  // The interior span (inches) available for a partition of direction `dir`.
  // width -> spans across width (x); depth -> across depth (z); shelf -> up height (y).
  // Positions clamp to [0, the resolved usable interior span] (T-03-05).
  _partitionSpan(dir) {
    const interior = this._resolved || resolveCaseDesign(this.state, { maxDim: this.limits.maxDim });
    if (dir === 'depth') return interior.interior.d;
    if (dir === 'shelf') return interior.interior.h;
    return interior.interior.w;
  }

  _renderPartitions() {
    const mountEl = this.querySelector('[data-mount="partitions"]');
    if (!mountEl) return;
    const tab = this._partitionTab === 'lid' ? 'lid' : 'base';
    const rows = this.state.partitions[tab] || [];

    // Reflect the active tab on the tab buttons (aria-selected + is-selected).
    this.querySelectorAll('[data-partition-tab]').forEach((btn) => {
      const active = btn.dataset.partitionTab === tab;
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.classList.toggle('is-selected', active);
      btn.tabIndex = active ? 0 : -1;
    });

    if (!rows.length) {
      const empty = document.createElement('p');
      empty.className = 'cased__caption cased__partition-empty';
      empty.textContent = 'No partitions yet. Add one to divide this section.';
      mountEl.replaceChildren(empty);
    } else {
      const dirs = [
        { id: 'width', label: 'Width' },
        { id: 'depth', label: 'Depth' },
        { id: 'shelf', label: 'Shelf' },
      ];
      const nodes = rows.map((row, index) => {
        const wrap = document.createElement('div');
        wrap.className = 'cased__partition-row';
        wrap.dataset.partitionIndex = String(index);

        // Direction pill group (one shared radio name for browser exclusivity).
        const dirGroup = document.createElement('div');
        dirGroup.className = 'cased__partition-dirs';
        dirGroup.setAttribute('role', 'radiogroup');
        dirGroup.setAttribute('aria-label', 'Partition direction');
        const dirName = 'partdir-' + tab + '-' + index + '-' + this._uid();
        dirs.forEach((d) => {
          const pill = document.createElement('label');
          pill.className = 'cased__pill cased__partition-dir';
          if (d.id === row.dir) pill.classList.add('is-selected');
          const dinput = document.createElement('input');
          dinput.type = 'radio';
          dinput.className = 'cased__pill-input';
          dinput.name = dirName;
          dinput.dataset.partitionField = 'dir';
          dinput.dataset.partitionTab = tab;
          dinput.dataset.partitionIndex = String(index);
          dinput.dataset.id = d.id;
          dinput.checked = d.id === row.dir;
          const dtext = document.createElement('span');
          dtext.className = 'cased__pill-label';
          dtext.textContent = d.label;
          pill.appendChild(dinput);
          pill.appendChild(dtext);
          dirGroup.appendChild(pill);
        });

        // Position (in) numeric input.
        const posField = document.createElement('div');
        posField.className = 'cased__field cased__partition-pos';
        const posLabel = document.createElement('label');
        posLabel.className = 'cased__label';
        const posId = 'CasedPartPos-' + tab + '-' + index + '-' + this._uid();
        posLabel.setAttribute('for', posId);
        posLabel.textContent = 'Position (in)';
        const posInput = document.createElement('input');
        posInput.type = 'number';
        posInput.className = 'cased__number';
        posInput.id = posId;
        posInput.min = '0';
        posInput.max = String(this._partitionSpan(row.dir));
        posInput.step = '1';
        posInput.value = String(row.pos);
        posInput.dataset.partitionField = 'pos';
        posInput.dataset.partitionTab = tab;
        posInput.dataset.partitionIndex = String(index);
        const posErr = document.createElement('p');
        posErr.className = 'cased__error cased__partition-error';
        posErr.setAttribute('data-partition-error', '');
        posErr.hidden = true;
        posErr.textContent = 'Enter a value within the case interior';
        posField.appendChild(posLabel);
        posField.appendChild(posInput);
        posField.appendChild(posErr);

        // Icon-only Remove button (immediate, no confirmation — UI-SPEC).
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cased__partition-remove';
        removeBtn.setAttribute('aria-label', 'Remove partition');
        removeBtn.dataset.partitionRemove = '';
        removeBtn.dataset.partitionTab = tab;
        removeBtn.dataset.partitionIndex = String(index);
        const removeIcon = this._removeIcon();
        if (removeIcon) {
          removeIcon.setAttribute('aria-hidden', 'true');
          removeBtn.appendChild(removeIcon);
        } else {
          removeBtn.textContent = '×';
        }

        wrap.appendChild(dirGroup);
        wrap.appendChild(posField);
        wrap.appendChild(removeBtn);
        return wrap;
      });
      mountEl.replaceChildren(...nodes);
    }

    // Add-partition control state: disabled at 12 with the cap note (UI-SPEC).
    const addBtn = this.querySelector('[data-partition-add]');
    const capNote = this.querySelector('[data-partition-cap]');
    const atCap = rows.length >= this.PARTITION_MAX;
    if (addBtn) addBtn.disabled = atCap;
    if (capNote) capNote.hidden = !atCap;
  }

  // Clone the server-rendered inline remove icon out of the hidden template. The
  // SVG is author-controlled markup (no innerHTML on any dynamic string, V5).
  _removeIcon() {
    if (this._removeIconTemplate === undefined) {
      const tpl = this.querySelector('template[data-remove-icon]');
      this._removeIconTemplate = tpl ? tpl.content : null;
    }
    if (!this._removeIconTemplate) return null;
    const source = this._removeIconTemplate.firstElementChild;
    return source ? source.cloneNode(true) : null;
  }

  // Shared update tail run after every partition mutation.
  _afterPartitionChange(highFrequency) {
    if (this._quoteSent) this._resetQuoteForm();
    this._updateSummaries();
    this._updateDesignSummary();
    this._updateEstimate();
    if (this.engine) {
      this._syncEngineLabels();
      if (highFrequency) this._rebuild();
      else this.engine.updateConfig(this.state);
    }
    this._persist();
  }

  _activatePartitionTab(tab) {
    this._partitionTab = tab === 'lid' ? 'lid' : 'base';
    this._renderPartitions();
  }

  // X-ray auto-engage (D-07). On expand: save the pre-expand X-ray value into the
  // DISTINCT scratch field _xrayBeforePartitions (never touching the engine's own
  // this._xray source of truth) and force X-ray ON. On collapse: restore exactly the
  // saved pre-expand value — never a forced off, so a manually-on X-ray (Plan 05
  // toolbar) survives (Pitfall 4). All engine calls guarded so the step works with
  // the canvas dead.
  _handlePartitionsToggle() {
    if (!this.partitionsStep) return;
    if (this.partitionsStep.open) {
      this._xrayBeforePartitions = this.engine ? this.engine._xray : false;
      if (this.engine) this.engine.setXray(true);
    } else {
      const restore = this._xrayBeforePartitions === true;
      if (this.engine) this.engine.setXray(restore);
    }
    // The auto-engage just moved the shared this.engine._xray boolean — reflect it
    // on the toolbar X-ray toggle so the two drivers never desync (D-07, Pitfall 4).
    this._syncXrayToolbar();
  }

  _handleAddPartition() {
    const tab = this._partitionTab === 'lid' ? 'lid' : 'base';
    const rows = this.state.partitions[tab];
    if (rows.length >= this.PARTITION_MAX) return;
    // New rows default to a width divider at the interior midpoint.
    const span = this._partitionSpan('width');
    rows.push({ dir: 'width', pos: Math.round(span / 2) });
    this._renderPartitions();
    this._afterPartitionChange(false);
  }

  _handleRemovePartition(tab, index) {
    const rows = this.state.partitions[tab];
    if (!rows || index < 0 || index >= rows.length) return;
    // Immediate remove — no confirmation dialog (UI-SPEC destructive rule).
    rows.splice(index, 1);
    this._renderPartitions();
    this._afterPartitionChange(false);
  }

  // Direction change: mutate the row and re-clamp its position to the new span.
  _handlePartitionDir(tab, index, dir) {
    const rows = this.state.partitions[tab];
    if (!rows || !rows[index]) return;
    rows[index].dir = dir;
    const span = this._partitionSpan(dir);
    if (rows[index].pos > span) rows[index].pos = span;
    this._renderPartitions();
    this._afterPartitionChange(false);
  }

  // Position edit reuses the _handleDimChange clamp shape: clamp to [0, span];
  // NaN falls back to the prior value; inline red note shows only while invalid.
  _handlePartitionPos(input, tab, index) {
    const rows = this.state.partitions[tab];
    if (!rows || !rows[index]) return;
    const span = this._partitionSpan(rows[index].dir);
    const raw = parseInt(input.value, 10);
    const prior = rows[index].pos;
    const invalid = !isNaN(raw) && (raw < 0 || raw > span);
    const val = Math.max(0, Math.min(span, isNaN(raw) ? prior : raw));
    rows[index].pos = val;
    // Toggle the inline error only while the typed value is out of range (V5).
    const errEl = input.parentElement
      ? input.parentElement.querySelector('[data-partition-error]')
      : null;
    if (errEl) errEl.hidden = !invalid;
    this._afterPartitionChange(true);
  }

  // Delegated: direction radio `change`.
  _handlePartitionChange(event) {
    const el = event.target;
    if (!el || !el.dataset || el.dataset.partitionField !== 'dir') return;
    this._handlePartitionDir(el.dataset.partitionTab, parseInt(el.dataset.partitionIndex, 10), el.dataset.id);
  }

  // Delegated: position number `input` (high-frequency → debounced rebuild).
  _handlePartitionInput(event) {
    const el = event.target;
    if (!el || !el.dataset || el.dataset.partitionField !== 'pos') return;
    this._handlePartitionPos(el, el.dataset.partitionTab, parseInt(el.dataset.partitionIndex, 10));
  }

  // Delegated: tab buttons, add button, remove buttons `click`.
  _handlePartitionClick(event) {
    const el = event.target.closest
      ? event.target.closest('[data-partition-tab],[data-partition-add],[data-partition-remove]')
      : null;
    if (!el) return;
    // Direction pill inputs also carry data-partition-tab; ignore those here
    // (they are handled on `change`, and a click that toggles a radio also fires
    // change). Only act on <button> tab controls.
    if (el.tagName === 'BUTTON' && el.dataset.partitionTab != null && el.dataset.partitionRemove == null && el.dataset.partitionAdd == null) {
      event.preventDefault();
      this._activatePartitionTab(el.dataset.partitionTab);
      return;
    }
    if (el.dataset.partitionAdd != null) {
      event.preventDefault();
      this._handleAddPartition();
      return;
    }
    if (el.dataset.partitionRemove != null) {
      event.preventDefault();
      this._handleRemovePartition(el.dataset.partitionTab, parseInt(el.dataset.partitionIndex, 10));
    }
  }

  _handleSidebarChange(event) {
    const input = event.target;
    const group = input.dataset && input.dataset.group;
    if (!group) return;

    // Changing the design after a confirmed send re-opens the form (contact
    // fields retained) so an updated quote can go out (D-13).
    if (this._quoteSent) this._resetQuoteForm();

    if (group === 'options') {
      const id = input.dataset.id;
      const idx = this.state.options.indexOf(id);
      if (input.checked && idx === -1) this.state.options.push(id);
      else if (!input.checked && idx !== -1) this.state.options.splice(idx, 1);
    } else {
      if (input.dataset.locked === 'true') {
        input.checked = true;
        return;
      }
      this.state[group] = input.dataset.id;
      this._reflectSelection(group, input.dataset.id);
      if (group === 'color') this._syncColorHex();
      if (group === 'finish') {
        this._reconcileColorToFinish();
        this._renderColors();
      }
      if (group === 'dimType') this._updateDimHelp();
      if (group === 'style') this._applyStyleGate(input.dataset.id, { applyDefault: true });
      if (group === 'table') this._syncTablePreview();
    }

    this._resolvePhysicalState();

    this._updateSummaries();
    this._updateDesignSummary();
    this._updateEstimate();
    this._updateItemFit();
    if (this.engine) {
      this._syncEngineLabels();
      this.engine.updateConfig(this.state);
    }
    this._persist();
  }

  // Style gate controls only style-specific UI. Rack shells remain configurable; the
  // resolver keeps the internal suspended rack at the physical 19-inch standard.
  _applyStyleGate(styleId, { applyDefault = false } = {}) {
    if (applyDefault && styleId === 'frontlid') {
      // A user selecting Front lid starts from the documented Fiasco envelope.
      // Deep links and saved/manual Front-lid dimensions never call this branch.
      this.state.w = 30;
      this.state.d = 24;
      this.state.h = 52.6;
      this.state.dimType = 'outside-of-case';
      this._reflectSelection('dimType', this.state.dimType);
      this._updateDimHelp();
    }
    this._setOtherNotesVisible(styleId === 'other');
    this._syncTableStep(styleId);
  }

  // Show the fold-out table step only for the front-lid style. Off any other
  // style the option cannot exist, so force "No table" (and drop any live
  // preview) and collapse the step. Always reconciles the preview.
  _syncTableStep(styleId = this.state.style) {
    const isFrontlid = styleId === 'frontlid';
    if (this.tableStep) this.tableStep.hidden = !isFrontlid;
    if (!isFrontlid) {
      if (this.state.table && this.state.table !== 'none') {
        this.state.table = 'none';
        // Re-render so the "No table" radio's checked state matches (a bare
        // wrapper-class toggle would leave the input stale).
        this._renderCards('tables', this.tableMount, 'table', 'card');
      }
      if (this.tableStep) this.tableStep.open = false;
    }
    this._syncTablePreview();
  }

  // Choosing a side IS the preview: pick Left or Right and the lid deploys as a
  // table, pick "No table" and it folds back. There is no separate checkbox --
  // two controls for one decision could disagree, and the state that mattered
  // (which side the table is on) was already the card selection. Guarded
  // (ENGINE-03): a no-op until/if the 3D engine boots, which is why the boot
  // path calls this too, so a `?tb=right` deep link opens already deployed.
  _syncTablePreview() {
    const deployed = this.state.table === 'left' || this.state.table === 'right';
    if (this.engine) this.engine.setTablePreview(deployed);
  }

  _setOtherNotesVisible(visible) {
    if (!this.otherNotesField) return;
    if (visible) this.otherNotesField.removeAttribute('hidden');
    else this.otherNotesField.setAttribute('hidden', '');
  }

  // Store the free-text "other" notes on state via value only — never innerHTML (V5).
  _handleOtherNotes(event) {
    if (this._quoteSent) this._resetQuoteForm();
    this.state.otherNotes = event.target.value;
    this._updateSummaries();
    this._updateDesignSummary();
    this._updateEstimate();
    if (this.engine) {
      this._syncEngineLabels();
      this.engine.updateConfig(this.state);
    }
    this._persist();
  }

  // ---- Item-fit (CONFIG-08, D-08/D-09) --------------------------------
  // A green/amber/red "will my item fit?" check driven by a single CLEARANCE_IN
  // constant, shared with the engine ghost. Verdict text is set via textContent only
  // (V5 / T-03-06); numeric inputs clamp defensively (T-03-07). Everything works with
  // the canvas dead — engine calls are guarded with `if (this.engine)`.

  _handleItemFitToggle(event) {
    if (this._quoteSent) this._resetQuoteForm();
    this.state.itemFit.on = !!event.target.checked;
    if (this.itemFitFields) this.itemFitFields.hidden = !this.state.itemFit.on;
    this._updateItemFit();
    if (this.engine) {
      this._syncEngineLabels();
      this.engine.updateConfig(this.state);
    }
    this._persist();
  }

  _handleItemFitInput(event) {
    if (this._quoteSent) this._resetQuoteForm();
    const field = event.target.dataset.itemFit;
    if (!field) return;
    if (field === 'desc') {
      // Free text — value only, never innerHTML (V5 / T-03-06).
      this.state.itemFit.desc = event.target.value;
    } else {
      // W/D/H reuse the _handleDimChange clamp shape: clamp to [minDim, maxDim];
      // an empty/NaN input clears the axis so the verdict degrades gracefully.
      const raw = parseInt(event.target.value, 10);
      if (isNaN(raw)) {
        this.state.itemFit[field] = null;
      } else {
        const min = this.limits.minDim;
        const max = this.limits.maxDim;
        this.state.itemFit[field] = Math.max(min, Math.min(max, raw));
      }
    }
    this._updateItemFit();
    if (this.engine) {
      this._syncEngineLabels();
      this.engine.updateConfig(this.state);
    }
    this._persist();
  }

  // Interior span (inches) available to the item per axis from the shared resolver.
  _interiorSpan() {
    const resolved = this._resolved || resolveCaseDesign(this.state, { maxDim: this.limits.maxDim });
    return { ...resolved.interior };
  }

  _resolvePhysicalState() {
    const resolved = resolveCaseDesign(this.state, { maxDim: this.limits.maxDim });
    this._resolved = resolved;
    this.state.style = resolved.style;
    this.state.dimType = resolved.dimType;
    this.state.w = resolved.requested.w;
    this.state.d = resolved.requested.d;
    this.state.h = resolved.requested.h;
    this._boundInputs.forEach((input) => {
      const axis = input.dataset.dim;
      if (!axis) return;
      input.min = String(resolved.minimumRequested[axis]);
      input.max = String(this.limits.maxDim);
      input.value = String(this.state[axis]);
    });
    return resolved;
  }

  // Compute + render the verdict. Called on every item-fit mutation AND after any
  // dimension/style change so the verdict stays live as the case itself resizes.
  _updateItemFit() {
    const badge = this.itemFitVerdict;
    const readout = this.itemFitClearance;
    const fit = this.state.itemFit;
    if (!fit || !fit.on || !(fit.w > 0) || !(fit.d > 0) || !(fit.h > 0)) {
      if (badge) {
        badge.hidden = true;
        badge.textContent = '';
        badge.style.removeProperty('--fit-color');
      }
      if (readout) {
        readout.hidden = true;
        readout.textContent = '';
      }
      return;
    }

    const interior = this._interiorSpan();
    const clrW = interior.w - fit.w;
    const clrD = interior.d - fit.d;
    const clrH = interior.h - fit.h;
    const min = Math.min(clrW, clrD, clrH);

    let sentence;
    let hex;
    if (min < 0) {
      sentence = "Won't fit — your item is larger than the interior";
      hex = FIT_HEX.red;
    } else if (min < CLEARANCE_IN) {
      sentence = "Fits — but it's a tight squeeze";
      hex = FIT_HEX.amber;
    } else {
      sentence = 'Fits with room to spare';
      hex = FIT_HEX.green;
    }

    if (badge) {
      badge.hidden = false;
      badge.textContent = sentence; // textContent only (V5 / T-03-06)
      badge.style.setProperty('--fit-color', hex);
    }
    if (readout) {
      readout.hidden = false;
      const fmt = (n) => (Math.round(n * 10) / 10) + '"';
      readout.textContent =
        'Clearance: ' + fmt(clrW) + ' W · ' + fmt(clrD) + ' D · ' + fmt(clrH) + ' H';
    }
  }

  _reflectSelection(group, id) {
    const inputs = this.querySelectorAll('input[data-group="' + group + '"]');
    inputs.forEach((input) => {
      const wrapper = input.closest('.cased__card, .cased__swatch, .cased__pill');
      if (wrapper) wrapper.classList.toggle('is-selected', input.dataset.id === id);
    });
  }

  _reconcileColorToFinish() {
    const valid = (this.catalogs.colors || []).filter(
      (c) => Array.isArray(c.finishes) && c.finishes.includes(this.state.finish)
    );
    if (!valid.length) return;
    const stillValid = valid.some((c) => c.id === this.state.color);
    if (!stillValid) this.state.color = valid[0].id;
    this._syncColorHex();
  }

  // Resolve the selected color id to its catalog hex so the leaf engine can paint
  // panels without depending on the catalogs (state carries the resolved hex).
  _syncColorHex() {
    const entry = (this.catalogs.colors || []).find((c) => c.id === this.state.color);
    this.state.colorHex = entry ? entry.hex : null;
  }

  // Carry resolved catalog labels in state so the engine's ARIA label matches the
  // sidebar summary wording without the leaf engine depending on the catalogs.
  _syncEngineLabels() {
    this.state.styleLabel = this._labelFor('styles', this.state.style);
    this.state.finishLabel = this._labelFor('finishes', this.state.finish);
    this.state.colorLabel = this._labelFor('colors', this.state.color);
  }

  _updateDimHelp() {
    if (!this.dimHelp) return;
    const dt = (this.catalogs.dimTypes || []).find((x) => x.id === this.state.dimType);
    this.dimHelp.textContent = dt ? dt.helper : '';
  }

  _labelFor(catalogKey, id) {
    const item = (this.catalogs[catalogKey] || []).find((x) => x.id === id);
    return item ? item.label : id;
  }

  _updateSummaries() {
    this._setSummary('style', 'Style', this._labelFor('styles', this.state.style));
    this._setSummary(
      'dimensions',
      'Dimensions',
      this.state.w + ' × ' + this.state.d + ' × ' + this.state.h + ' in'
    );
    this._setSummary('plywood', 'Plywood', this._labelFor('plywoods', this.state.plywood));
    this._setSummary(
      'finish',
      'Finish',
      this._labelFor('finishes', this.state.finish) + ' · ' + this._labelFor('colors', this.state.color)
    );
    this._setSummary('lining', 'Lining', this._labelFor('linings', this.state.lining));
    this._setSummary('wheels', 'Wheels', this._labelFor('wheels', this.state.wheels));
    this._setSummary('table', 'Table', this._labelFor('tables', this.state.table));

    const hardware = this.state.options.length
      ? this.state.options.map((id) => this._labelFor('options', id)).join(', ')
      : 'None';
    this._setSummary('hardware', 'Hardware', hardware);

    const pb = (this.state.partitions && this.state.partitions.base) || [];
    const pl = (this.state.partitions && this.state.partitions.lid) || [];
    this._setSummary(
      'partitions',
      'Partitions',
      pb.length + ' in base · ' + pl.length + ' in lid'
    );
  }

  _setSummary(key, group, value) {
    const el = this.querySelector('[data-summary="' + key + '"]');
    if (!el) return;
    el.replaceChildren();
    const strong = document.createElement('span');
    strong.className = 'cased__summary-value';
    strong.textContent = value;
    el.append(group + ': ', strong);
  }

  _updateDesignSummary() {
    const parts = [
      this._labelFor('styles', this.state.style) + ' case',
      this.state.w + ' × ' + this.state.d + ' × ' + this.state.h + ' in',
      this._labelFor('finishes', this.state.finish) + ', ' + this._labelFor('colors', this.state.color),
      this._labelFor('wheels', this.state.wheels),
    ];
    const text = parts.join(' · ');
    if (this.designSummaries) {
      this.designSummaries.forEach((el) => {
        el.textContent = text;
      });
    }
  }

  // Pure arithmetic over this.state — no engine dependency, so the estimate
  // works even when the 3D canvas is dead (progressive enhancement / ENGINE-03).
  // Finish carries no upcharge (Mat Black == Durable Hex per D-10). Anchors at
  // seed rates: 24×48×30 → surface 46 sq ft → 160+14·46 = 804 ≈ $800;
  // 24×24×30 → surface 28 sq ft → 160+14·28 = 552 ≈ $550. Rates are
  // merchant-tunable in the theme editor (D-07, A1).
  _computeEstimate() {
    const { w, d, h } = (this._resolved || this._resolvePhysicalState()).exterior;
    const p = this.pricing;
    const surfaceSqFt = (2 * (w * d + w * h + d * h)) / 144;
    let mid = p.base + p.perSqFt * surfaceSqFt;
    if (this.state.lining === 'carpet') mid += p.liningCarpetPerSqFt * surfaceSqFt;
    if (this.state.lining === 'custom-foam') mid += p.liningFoamFlat;
    // A caster is always present (both wheel options are a caster set).
    mid += p.castersFlat;
    const flats = {
      handles: p.handlesFlat,
      'recessed-hardware': p.recessedFlat,
      'padlock-latches': p.padlockFlat,
      'stacking-plates': p.stackingFlat,
    };
    this.state.options.forEach((id) => {
      mid += flats[id] || 0;
    });
    // Fold-out table + folding legs are one bundled priced feature (frontlid only;
    // the resolver guarantees table is 'none' off frontlid).
    if (this.state.table && this.state.table !== 'none') mid += p.tableFoldOutFlat || 0;
    const band = p.rangeBand;
    const round5 = (n) => Math.round(n / 5) * 5;
    return { low: round5(mid * (1 - band)), high: round5(mid * (1 + band)) };
  }

  // Range only, never a cost breakdown (D-08). Always prefixed "Estimated"
  // (UI-SPEC mandatory). textContent only — never innerHTML (V5 XSS mitigation).
  _updateEstimate() {
    if (!this.estimateNodes) return;
    const { low, high } = this._computeEstimate();
    const text = 'Estimated: $' + low.toLocaleString() + ' – $' + high.toLocaleString();
    this.estimateNodes.forEach((el) => {
      el.textContent = text;
    });
  }

  // ---- Quote submission (Plan 02-02) ------------------------------------

  // Deep link for the email + Phase 4 restore. Writes ONLY non-default values,
  // compact, using the LOCKED key contract Phase 4's decoder reads — do NOT
  // rename these keys after ship: w,d,h,s,dt,f,c,py,ln,wh,o (D-05, SHARE-01).
  _serializeDeepLink() {
    const p = new URLSearchParams();
    p.set('v', String(CASE_SCHEMA_VERSION));
    const dv = this._defaults || {};
    const put = (k, v, d) => {
      if (v !== d && v != null && v !== '') p.set(k, String(v));
    };
    put('w', this.state.w, dv.w);
    put('d', this.state.d, dv.d);
    put('h', this.state.h, dv.h);
    put('s', this.state.style, dv.style);
    put('dt', this.state.dimType, dv.dimType);
    put('f', this.state.finish, dv.finish);
    put('c', this.state.color, dv.color);
    put('py', this.state.plywood, dv.plywood);
    put('ln', this.state.lining, dv.lining);
    put('wh', this.state.wheels, dv.wheels);
    if (this.state.options && this.state.options.length) {
      p.set('o', this.state.options.join(','));
    }
    // --- APPEND Phase-3 keys below the LOCKED set (never rename above) ---
    // on = "other" custom notes (only present when the Other style carries notes).
    if (this.state.style === 'other' && (this.state.otherNotes || '').trim()) {
      p.set('on', this.state.otherNotes.trim());
    }
    // Fold-out table (frontlid only): tb = left|right. Non-default only ('none'
    // is the default and is omitted). The decoder + resolver both re-gate to
    // frontlid, so a tb key on a non-frontlid link is harmless.
    if (this.state.table && this.state.table !== 'none') p.set('tb', this.state.table);
    // Partitions (CONFIG-07): pb = base, pl = lid, each encoded as `dir,pos;dir,pos`
    // (build-spec §4 scheme, e.g. `w,7;d,12`). dir compacted to its first letter
    // (w/d/s). Non-defaults only — an empty tab omits its key. Phase 4's decoder
    // clamps pos to [4,96] and falls back to defaults for unknown dir ids (T-03-09).
    const encodeParts = (rows) =>
      (rows || [])
        .filter((r) => r && r.dir != null && r.pos != null)
        .map((r) => String(r.dir).charAt(0) + ',' + r.pos)
        .join(';');
    const pb = encodeParts(this.state.partitions && this.state.partitions.base);
    const pl = encodeParts(this.state.partitions && this.state.partitions.lid);
    if (pb) p.set('pb', pb);
    if (pl) p.set('pl', pl);
    // Item-fit (CONFIG-08): if = `w,d,h` (item dims), with a trailing `,1` when the
    // toggle is on. Encoded only when the check is on AND all three dims are present.
    const fit = this.state.itemFit;
    if (fit && fit.on && fit.w != null && fit.d != null && fit.h != null &&
        fit.w !== '' && fit.d !== '' && fit.h !== '') {
      p.set('if', fit.w + ',' + fit.d + ',' + fit.h + ',1');
    }
    // NOTE: style still rides the LOCKED `s` key — the new style ids (e.g.
    // continental) only widen its value space; NO second style key is added.
    const qs = p.toString();
    return location.origin + location.pathname + (qs ? '?' + qs : '');
  }

  // The exact inverse of _serializeDeepLink (SHARE-01). Deep-link params are
  // ATTACKER-CONTROLLABLE untrusted input (a crafted link can be sent to a victim),
  // so every value is whitelisted / clamped before it touches state, and every
  // assignment is to state fields only (never innerHTML / DOM here) — the file's V5
  // contract (T-04-01/02/03/04). Overlays onto the already-defaults-shaped this.state
  // so any absent key keeps its default automatically. Returns a didFallback boolean
  // (true when an unknown/removed catalog id fell back to a default, D-10).
  _decodeDeepLink(searchString) {
    const params = new URLSearchParams(searchString);
    const st = this.state;
    const cat = this.catalogs || {};
    const minDim = this.limits.minDim;
    const maxDim = this.limits.maxDim;
    const inCatalog = (catKey, id) => (cat[catKey] || []).some((x) => x.id === id);
    let didFallback = false;
    const linkVersion = parseInt(params.get('v') || '1', 10);
    if (linkVersion < 2 && !params.has('dt')) st.dimType = 'interior';

    // 1. Dims FIRST — _partitionSpan (used for partition pos clamp below) reads them.
    //    parseInt + clamp to [minDim, maxDim]; a present-but-NaN value keeps the
    //    default. Clamping is not a "reset to default", so it never sets didFallback.
    ['w', 'd', 'h'].forEach((k) => {
      if (!params.has(k)) return;
      const raw = parseInt(params.get(k), 10);
      if (isNaN(raw)) return;
      st[k] = Math.max(minDim, Math.min(maxDim, raw));
    });

    // 2. Style next — legacy taxonomy is migrated before catalog validation.
    if (params.has('s')) {
      const v = params.get('s') === 'split5050' ? 'liftoff' : params.get('s');
      if (inCatalog('styles', v)) st.style = v;
      else didFallback = true;
    }

    // 3. Remaining single-id catalog fields — whitelist each against its catalog;
    //    an unknown/removed id keeps the default and flags the reset notice (D-10).
    const single = [
      ['dt', 'dimType', 'dimTypes'],
      ['f', 'finish', 'finishes'],
      ['c', 'color', 'colors'],
      ['py', 'plywood', 'plywoods'],
      ['ln', 'lining', 'linings'],
      ['wh', 'wheels', 'wheels'],
      ['tb', 'table', 'tables'],
    ];
    single.forEach((row) => {
      const key = row[0];
      const field = row[1];
      const catKey = row[2];
      if (!params.has(key)) return;
      const v = params.get(key);
      if (inCatalog(catKey, v)) st[field] = v;
      else didFallback = true;
    });

    // options: o = id,id,id — filter to ids present in the catalog; a dropped id flags.
    if (params.has('o')) {
      const raw = String(params.get('o'))
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
      const valid = raw.filter((id) => inCatalog('options', id));
      if (valid.length !== raw.length) didFallback = true;
      st.options = valid;
    }

    // on: free-text "other" notes — string value only (never innerHTML). Visibility is
    // gated later by _applyStyleGate (revealed only when style === 'other').
    if (params.has('on')) {
      st.otherNotes = String(params.get('on'));
    }

    // if: item-fit w,d,h,flag. w/d/h parseInt + clamp [minDim,maxDim]; trailing 1 = on.
    // desc is never carried by the encoder (A4) — leave whatever is already on state.
    if (params.has('if')) {
      if (!st.itemFit || typeof st.itemFit !== 'object') {
        st.itemFit = { on: false, w: null, d: null, h: null, desc: '' };
      }
      const parts = String(params.get('if')).split(',');
      const clampDim = (s) => {
        const raw = parseInt(s, 10);
        return isNaN(raw) ? null : Math.max(minDim, Math.min(maxDim, raw));
      };
      st.itemFit.w = clampDim(parts[0]);
      st.itemFit.d = clampDim(parts[1]);
      st.itemFit.h = clampDim(parts[2]);
      st.itemFit.on = parts[3] === '1';
    }

    // 4. Partitions LAST — _partitionSpan(dir) depends on the dims + style decoded
    //    above. dir letter w/d/s -> width/depth/shelf (unknown letter -> drop the row);
    //    pos = round + clamp to [0, _partitionSpan(dir)] (T-04-04, NOT [4,96] — the
    //    encoder comment is stale, A1); each tab truncated to PARTITION_MAX (T-04-03).
    const dirMap = { w: 'width', d: 'depth', s: 'shelf' };
    const decodeTab = (raw) => {
      const out = [];
      String(raw)
        .split(';')
        .forEach((chunk) => {
          if (!chunk) return;
          const bits = chunk.split(',');
          const dir = dirMap[String(bits[0]).charAt(0)];
          if (!dir) return;
          const rawPos = parseInt(bits[1], 10);
          if (isNaN(rawPos)) return;
          const span = this._partitionSpan(dir);
          out.push({ dir: dir, pos: Math.round(Math.max(0, Math.min(span, rawPos))) });
        });
      return out.slice(0, this.PARTITION_MAX);
    };
    if (params.has('pb')) st.partitions.base = decodeTab(params.get('pb'));
    if (params.has('pl')) st.partitions.lid = decodeTab(params.get('pl'));

    return didFallback;
  }

  // The ONE shared state->DOM re-hydration path. Re-renders every catalog control from
  // state (the sanctioned path — NEVER dispatch synthetic change/input events, which
  // would re-enter _handleSidebarChange, reset the quote form, and hit the Plan 01-04
  // radio-name landmine), pushes the three Liquid-static input groups _renderSidebar()
  // does not own, and ends with a guarded engine tail so it fully works with the canvas
  // dead (ENGINE-03). Reused by deep-link restore now, and by load/autosave in Slice 2.
  _applyStateToDom() {
    this._resolvePhysicalState();
    this._syncColorHex();
    this._renderSidebar();
    // A decoded/loaded finish can invalidate the color — snap it to a valid one first,
    // then re-render the (finish-dependent) color swatches (Pitfall 5).
    this._reconcileColorToFinish();
    this._renderColors();
    // Apply style-specific UI visibility after state normalization.
    this._applyStyleGate(this.state.style);

    // Push the three Liquid-static input groups _renderSidebar() does not regenerate.
    this._boundInputs.forEach((input) => {
      const dim = input.dataset.dim;
      if (dim) input.value = String(this.state[dim]);
    });
    const fit = this.state.itemFit || {};
    if (this.itemFitToggle) this.itemFitToggle.checked = !!fit.on;
    if (this.itemFitFields) this.itemFitFields.hidden = !fit.on;
    if (this.itemFitInputs) {
      this.itemFitInputs.forEach((input) => {
        const field = input.dataset.itemFit;
        if (!field) return;
        const v = fit[field];
        input.value = v == null ? '' : String(v);
      });
    }
    if (this.otherNotesInput) this.otherNotesInput.value = this.state.otherNotes || '';

    // Update tail.
    this._updateSummaries();
    this._updateDesignSummary();
    this._updateEstimate();
    this._updateItemFit();

    // Engine tail — guarded (ENGINE-03): a no-op until/if the 3D engine boots.
    if (this.engine) {
      this._syncEngineLabels();
      this.engine.updateConfig(this.state);
    }
  }

  // Load-time precedence resolver (D-09). Runs in connectedCallback BEFORE the first
  // render. Deep link (any recognized key present) wins and overlays onto state via the
  // defensive decoder; a fallback queues the amber reset notice for after first render
  // (D-10). Deep link never writes autosave (D-08). The whole body is try/catch-wrapped
  // so a parse/storage failure never throws out of connectedCallback (Pitfall 7).
  _resolveLoadPrecedence() {
    try {
      const RECOGNIZED = [
        'v', 'w', 'd', 'h', 's', 'dt', 'f', 'c', 'py', 'ln', 'wh', 'o', 'on', 'pb', 'pl', 'if', 'tb',
      ];
      const params = new URLSearchParams(location.search);
      // Test recognized keys, NOT location.search !== '' — a bare ?utm_source=… must
      // not trigger a decode or a spurious reset notice (Pitfall 9).
      const hasDeepLink = RECOGNIZED.some((k) => params.has(k));
      if (hasDeepLink) {
        const didFallback = this._decodeDeepLink(location.search);
        if (didFallback) {
          this._pendingToast = { text: 'Some options were reset to defaults', tone: 'warn' };
        }
        // Slice 2 reads this to honor D-08 (deep link wins, autosave untouched).
        this._restoredFromDeepLink = true;
        return;
      }
      // Slice 2 autosave-restore branch (clean-URL return, D-07). No recognized deep-link
      // key is present, so if a versioned autosave payload exists silently overlay its
      // state onto this.state and queue the green "Restored" toast for after the first
      // render (no confirm prompt). _readAutosave already rejects a bad/wrong-version
      // payload as null, so a corrupt autosave just falls through to shipped defaults.
      const auto = this._readAutosave();
      if (auto && auto.state && typeof auto.state === 'object') {
        this.state = auto.state;
        this._pendingToast = { text: 'Restored your last design', tone: 'success' };
      }
    } catch (e) {
      // Never throw out of connectedCallback (Pitfall 7 / T-04-06).
    }
  }

  // ---- localStorage save/autosave layer (SHARE-02, Slice 2) -------------
  // Named local saves (cap 20, D-04) + a single versioned last-session autosave (D-07).
  // EVERY storage access is inside try/catch and every read is version-checked (v === 1)
  // so a corrupt/tampered/oversized payload is treated as absent — a storage error can
  // NEVER throw out of the element (T-04-07/08). All stored strings are rendered via
  // textContent only in _renderSaves (never innerHTML, T-04-09). Works with the canvas
  // dead — no engine gating anywhere in this layer.

  // Feature-detect writable localStorage once (memoized). Private/hardened modes throw on
  // setItem; any throw → false so the Save UI disables gracefully (Pitfall 7 / T-04-08).
  _lsAvailable() {
    if (this._lsOk !== undefined) return this._lsOk;
    try {
      const probe = '__caselabs_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      this._lsOk = true;
    } catch (e) {
      this._lsOk = false;
    }
    return this._lsOk;
  }

  // Thin defensive getter: returns the parsed object or null; never throws.
  _lsGet(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  // Thin defensive setter: JSON-stringifies + writes; swallows any throw (quota / mode).
  _lsSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  // Read the saves payload, always returning a valid `{ v: 1, items: [...] }`. A missing,
  // unparseable, or wrong-version payload is treated as absent (empty). items is truncated
  // to SAVES_MAX on read (defensive against a tampered/inflated array, T-04-10).
  _loadSaves() {
    const payload = this._lsGet(LS_SAVES_KEY);
    if (!payload || ![1, 2].includes(payload.v) || !Array.isArray(payload.items)) {
      return { v: CASE_SCHEMA_VERSION, items: [] };
    }
    const items = payload.items.slice(0, SAVES_MAX).map((item) => ({
      ...item,
      state: migrateCaseState(item.state, payload.v),
    }));
    const migrated = { v: CASE_SCHEMA_VERSION, items };
    if (payload.v !== CASE_SCHEMA_VERSION) this._lsSet(LS_SAVES_KEY, migrated);
    return migrated;
  }

  // Auto-name (D-05): `{W}×{D}×{H} · {Finish label}` (U+00D7 glyph, full catalog finish
  // label). Collisions against the already-saved names disambiguate with ` #2`, ` #3`, …
  // so two near-identical designs never share a label.
  _autoName(state) {
    const base =
      state.w + '×' + state.d + '×' + state.h +
      ' · ' + this._labelFor('finishes', state.finish);
    const existing = this._loadSaves().items.map((it) => it.name);
    if (existing.indexOf(base) === -1) return base;
    let n = 2;
    while (existing.indexOf(base + ' #' + n) !== -1) n++;
    return base + ' #' + n;
  }

  // Save the current design (D-04/D-05). No-op when storage is unavailable (the button is
  // already disabled). Blocks at the 20-cap with the exact amber notice and performs NO
  // write — never a silent overwrite of the oldest. The snapshot is a deep clone
  // (JSON round-trip) so nested partitions.base/lid + itemFit detach from the live state.
  _saveDesign() {
    if (!this._lsAvailable()) return;
    const payload = this._loadSaves();
    if (payload.items.length >= SAVES_MAX) {
      this._showToast(
        "You've saved 20 designs — the maximum. Delete one to save this design.",
        'warn'
      );
      return;
    }
    payload.items.push({
      id: String(Date.now()) + '-' + Math.random().toString(36).slice(2, 8),
      name: this._autoName(this.state),
      savedAt: Date.now(),
      state: JSON.parse(JSON.stringify(this.state)),
    });
    this._lsSet(LS_SAVES_KEY, payload);
    this._renderSaves();
  }

  // Delete a saved design by id (D-06). Immediate — no confirmation dialog. Rewrites
  // storage and re-renders the list.
  _deleteSave(id) {
    const payload = this._loadSaves();
    payload.items = payload.items.filter((it) => it.id !== id);
    this._lsSet(LS_SAVES_KEY, payload);
    this._renderSaves();
  }

  // Write the last-session autosave (D-07/D-11). No-op when storage is unavailable. Deep
  // clone so the stored snapshot detaches from the live state. Called from the debounced
  // _persist tail only (never at boot), so a pure deep-link visit leaves it untouched (D-08).
  _writeAutosave() {
    if (!this._lsAvailable()) return;
    this._lsSet(LS_AUTOSAVE_KEY, {
      v: CASE_SCHEMA_VERSION,
      savedAt: Date.now(),
      state: JSON.parse(JSON.stringify(this.state)),
    });
  }

  // Read the last-session autosave. Returns the payload, or null when absent / unparseable
  // / wrong-version (defensive, T-04-07).
  _readAutosave() {
    const payload = this._lsGet(LS_AUTOSAVE_KEY);
    if (!payload || ![1, 2].includes(payload.v) || !payload.state) return null;
    const migrated = {
      ...payload,
      v: CASE_SCHEMA_VERSION,
      state: migrateCaseState(payload.state, payload.v),
    };
    if (payload.v !== CASE_SCHEMA_VERSION) this._lsSet(LS_AUTOSAVE_KEY, migrated);
    return migrated;
  }

  // Rebuild the saved-designs list from storage (mirrors _renderPartitions). Empty →
  // a single muted caption (never a bare empty <div> — Taste div:empty{display:none},
  // Pitfall 5). Populated → one .cased__save-row per item with name (textContent, never
  // innerHTML — T-04-09), a date/time subtitle, and a Load + icon-only Delete action.
  _renderSaves() {
    const mountEl = this.savesMount || this.querySelector('[data-mount="saves"]');
    if (!mountEl) return;
    const items = this._loadSaves().items;

    if (!items.length) {
      const empty = document.createElement('p');
      empty.className = 'cased__caption cased__saves-empty';
      empty.textContent =
        'No saved designs yet. Save your current design to come back to it later.';
      mountEl.replaceChildren(empty);
    } else {
      const nodes = items.map((item) => {
        const row = document.createElement('div');
        row.className = 'cased__save-row';
        row.dataset.saveId = item.id;

        const meta = document.createElement('div');
        meta.className = 'cased__save-meta';
        const name = document.createElement('span');
        name.className = 'cased__save-name';
        name.textContent = item.name; // textContent only (V5 / T-04-09)
        const date = document.createElement('span');
        date.className = 'cased__save-date';
        date.textContent = new Date(item.savedAt).toLocaleString();
        meta.appendChild(name);
        meta.appendChild(date);

        const actions = document.createElement('div');
        actions.className = 'cased__save-actions';
        const loadBtn = document.createElement('button');
        loadBtn.type = 'button';
        loadBtn.className = 'button button--secondary cased__save-load';
        loadBtn.dataset.saveLoad = item.id;
        loadBtn.textContent = 'Load';
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'cased__save-delete';
        deleteBtn.setAttribute('aria-label', 'Delete saved design');
        deleteBtn.dataset.saveDelete = item.id;
        const removeIcon = this._removeIcon();
        if (removeIcon) {
          removeIcon.setAttribute('aria-hidden', 'true');
          deleteBtn.appendChild(removeIcon);
        } else {
          deleteBtn.textContent = '×';
        }
        actions.appendChild(loadBtn);
        actions.appendChild(deleteBtn);

        row.appendChild(meta);
        row.appendChild(actions);
        return row;
      });
      mountEl.replaceChildren(...nodes);
    }

    // Optional collapsed-step count summary ("{n} saved" / blank when none).
    const summary = this.querySelector('[data-summary="saves"]');
    if (summary) summary.textContent = items.length ? items.length + ' saved' : '';
  }

  // Load a saved design into the configurator (D-06). Replaces this.state with a deep
  // clone of the saved snapshot, then re-hydrates through the ONE shared _applyStateToDom
  // path — NO synthetic change/input events (RESEARCH Pattern 2). Non-destructive to
  // storage. Then persist so the loaded design becomes the live URL + autosave.
  _loadSave(id) {
    const item = this._loadSaves().items.find((it) => it.id === id);
    if (!item || !item.state) return;
    this.state = JSON.parse(JSON.stringify(item.state));
    if (this._quoteSent) this._resetQuoteForm();
    this._applyStateToDom();
    this._persist();
  }

  // Delegated click for the saved-designs list: Load restores, Delete removes immediately.
  _handleSavesClick(event) {
    const t = event.target;
    const loadBtn = t.closest ? t.closest('[data-save-load]') : null;
    if (loadBtn) {
      event.preventDefault();
      this._loadSave(loadBtn.dataset.saveLoad);
      return;
    }
    const deleteBtn = t.closest ? t.closest('[data-save-delete]') : null;
    if (deleteBtn) {
      event.preventDefault();
      this._deleteSave(deleteBtn.dataset.saveDelete);
    }
  }

  // Single reusable feedback toast (D-01/D-07/D-10). Sets textContent (never innerHTML)
  // on the one Liquid-authored [data-toast] element, applies the tone modifier, reveals
  // it, then auto-dismisses after ~2.5s. A newer toast replaces the current one (single
  // element, not a stack). No-op when the element is absent.
  _showToast(text, tone) {
    const el = this.toastEl || this.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = text;
    el.classList.remove('cased__toast--success', 'cased__toast--warn');
    el.classList.add(tone === 'success' ? 'cased__toast--success' : 'cased__toast--warn');
    el.classList.add('is-visible');
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
    }, 2500);
  }

  // Copy the current design's deep link to the clipboard (D-01). Prefers the async
  // Clipboard API in a secure context; otherwise (or on rejection) uses the temp-
  // textarea execCommand fallback. The URL is NEVER rendered on screen (D-01/D-03).
  _copyDesignLink() {
    const url = this._serializeDeepLink();
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(url)
        .then(() => this._showToast('Copied!', 'success'))
        .catch(() => this._fallbackCopy(url));
    } else {
      this._fallbackCopy(url);
    }
  }

  // Clipboard fallback for non-secure / older contexts: a readonly off-screen textarea
  // + document.execCommand('copy'). Degrades to amber guidance pointing at the address
  // bar (no visible URL field exists) and never throws out (T-04-06).
  _fallbackCopy(text) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const done = document.execCommand('copy');
      document.body.removeChild(ta);
      if (done) this._showToast('Copied!', 'success');
      else this._showToast('Press Ctrl/Cmd+C to copy', 'warn');
    } catch (e) {
      this._showToast('Copy not supported — select the link from your address bar.', 'warn');
    }
  }

  // Resolve labels via _labelFor and write the grouped machine-spec strings into
  // the hidden contact[...] fields by data-quote-field. Split into 5 groups so
  // each stays well under Shopify's ~255-char field limit (Pitfall 3). Values are
  // assigned to input.value (never innerHTML) — no injection surface (V5).
  _fillHiddenSpecFields() {
    if (!this.quoteForm) return;
    const set = (key, val) => {
      const el = this.quoteForm.querySelector('[data-quote-field="' + key + '"]');
      if (el) el.value = val;
    };
    set(
      'spec-dimensions',
      this.state.w + '×' + this.state.d + '×' + this.state.h +
        ' in requested (' + this._labelFor('dimTypes', this.state.dimType) + '); exterior ' +
        this._resolved.exterior.w + '×' + this._resolved.exterior.d + '×' + this._resolved.exterior.h +
        ' in; interior ' + this._resolved.interior.w + '×' + this._resolved.interior.d + '×' +
        this._resolved.interior.h + ' in'
    );
    const notes = (this.state.otherNotes || '').trim();
    set(
      'spec-construction',
      this._labelFor('styles', this.state.style) + ', ' +
        this._resolved.wall + '" walls, ' + this._resolved.floor + '" wheelboard, ' +
        this._labelFor('wheels', this.state.wheels) +
        (this.state.style === 'other' && notes ? ' — Notes: ' + notes : '')
    );
    set(
      'spec-finish',
      this._labelFor('finishes', this.state.finish) + ' · ' +
        this._labelFor('colors', this.state.color)
    );
    // Interior = lining + partitions + item-fit (each appended only when present so
    // the field stays compact and under Shopify's ~255-char limit). input.value only.
    const partsDesc = this._describePartitions();
    const fitDesc = this._describeItemFit();
    let interior = this._labelFor('linings', this.state.lining);
    if (partsDesc) interior += ' · Partitions — ' + partsDesc;
    if (fitDesc) interior += ' · ' + fitDesc;
    set('spec-interior', interior);
    set(
      'spec-hardware',
      this.state.options && this.state.options.length
        ? this.state.options.map((id) => this._labelFor('options', id)).join(', ')
        : 'None'
    );
    const { low, high } = this._computeEstimate();
    set('estimate', 'Estimated: $' + low.toLocaleString() + ' – $' + high.toLocaleString());
    set('deeplink', this._serializeDeepLink());
  }

  // Human-readable partitions summary for the quote spec (e.g.
  // "Base: 2 (Width @ 7 in, Depth @ 12 in); Lid: 1 (Shelf @ 10 in)"). Returns ''
  // when there are none so the caller can omit the line. Text only (V5).
  _describePartitions() {
    const dirLabel = { width: 'Width', depth: 'Depth', shelf: 'Shelf' };
    const one = (rows) =>
      (rows || [])
        .filter((r) => r && r.dir != null && r.pos != null)
        .map((r) => (dirLabel[r.dir] || r.dir) + ' @ ' + r.pos + ' in')
        .join(', ');
    const parts = this.state.partitions || {};
    const base = one(parts.base);
    const lid = one(parts.lid);
    const segs = [];
    if (base) segs.push('Base: ' + (parts.base.length) + ' (' + base + ')');
    if (lid) segs.push('Lid: ' + (parts.lid.length) + ' (' + lid + ')');
    return segs.join('; ');
  }

  // Human-readable item-fit summary for the quote spec (e.g.
  // "Item to fit: 20×30×10 in — Guitar amp"). Returns '' when the check is off or
  // dims are incomplete. Text only (V5).
  _describeItemFit() {
    const fit = this.state.itemFit;
    if (!fit || !fit.on) return '';
    if (fit.w == null || fit.d == null || fit.h == null || fit.w === '' || fit.d === '' || fit.h === '') {
      return '';
    }
    const desc = (fit.desc || '').trim();
    return 'Item to fit: ' + fit.w + '×' + fit.d + '×' + fit.h + ' in' + (desc ? ' — ' + desc : '');
  }

  // Human-readable multi-line spec for the mailto fallback body (D-06).
  _serializeSpec() {
    const { low, high } = this._computeEstimate();
    const notes = (this.state.otherNotes || '').trim();
    const lines = [
      'Dimensions: ' + this.state.w + '×' + this.state.d + '×' + this.state.h +
        ' in (' + this._labelFor('dimTypes', this.state.dimType) + ')',
      'Construction: ' + this._labelFor('styles', this.state.style) + ', ' +
        this._labelFor('plywoods', this.state.plywood) + ', ' +
        this._labelFor('wheels', this.state.wheels),
    ];
    if (this.state.style === 'other' && notes) {
      lines.push('Custom notes: ' + notes);
    }
    lines.push(
      'Finish & Color: ' + this._labelFor('finishes', this.state.finish) + ' · ' +
        this._labelFor('colors', this.state.color),
      'Interior: ' + this._labelFor('linings', this.state.lining)
    );
    if (this.state.table && this.state.table !== 'none') {
      lines.push('Fold-out table: ' + this._labelFor('tables', this.state.table) + ' (incl. folding legs)');
    }
    const partsDesc = this._describePartitions();
    if (partsDesc) lines.push('Partitions: ' + partsDesc);
    const fitDesc = this._describeItemFit();
    if (fitDesc) lines.push(fitDesc);
    lines.push(
      'Hardware: ' + (this.state.options && this.state.options.length
        ? this.state.options.map((id) => this._labelFor('options', id)).join(', ')
        : 'None'),
      'Estimated range: $' + low.toLocaleString() + ' – $' + high.toLocaleString(),
      'Design link: ' + this._serializeDeepLink()
    );
    return lines.join('\n');
  }

  // Enhanced stay-on-design submit (D-12). Additive over the native {% form %}
  // POST which remains the JS-dead fallback. The Shopify response HTML is parsed
  // with DOMParser and read via textContent only — NEVER injected into the live
  // DOM (V5 DOM-XSS). A missing success marker (captcha challenge / 429) falls
  // through to the mailto fallback — never a broken state (Pitfall 2).
  _handleQuoteSubmit(e) {
    if (this._submitting) return;
    if (!this.quoteForm) return;
    e.preventDefault();
    this._fillHiddenSpecFields();
    this._submitting = true;
    const btn = this.quoteForm.querySelector('.cased__quote-submit');
    const originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Sending…';
    }
    fetch(this.quoteForm.action, {
      method: 'POST',
      body: new FormData(this.quoteForm),
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => r.text().then((html) => ({ ok: r.ok, html })))
      .then(({ ok, html }) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const marker = doc.querySelector('.form-status');
        const success =
          ok && marker && /thank|success|on its way/i.test(marker.textContent || '');
        if (success) this._showQuoteSuccess();
        else this._showMailtoFallback();
      })
      .catch(() => this._showMailtoFallback())
      .finally(() => {
        this._submitting = false;
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.textContent = originalText || 'Request your quote';
        }
      });
  }

  // Swap the form for the confirmation in place, keeping the page + 3D design on
  // screen (D-12). Success node is announced via role="status".
  _showQuoteSuccess() {
    if (!this.quoteForm) return;
    let node = this.quoteForm.querySelector('.cased__quote-success');
    if (!node) {
      node = document.createElement('div');
      node.className = 'cased__quote-success';
      node.setAttribute('role', 'status');
      node.setAttribute('tabindex', '-1');
      const p = document.createElement('p');
      p.textContent =
        "Thanks — your spec is on its way to our team. We'll follow up with pricing shortly.";
      node.appendChild(p);
      this.quoteForm.insertBefore(node, this.quoteForm.firstChild);
    }
    node.classList.add('is-visible');
    this.quoteForm.classList.add('cased__quote-form--sent');
    const fb = this.quoteForm.querySelector('[data-quote-fallback]');
    if (fb) fb.setAttribute('hidden', '');
    this._quoteSent = true;
    if (typeof node.focus === 'function') node.focus();
  }

  // Amber pre-filled mailto banner so the customer is never stranded (D-06/D-02).
  // Subject and body are encodeURIComponent'd — no unescaped user input in the URL.
  _showMailtoFallback() {
    if (!this.quoteForm) return;
    const fb = this.quoteForm.querySelector('[data-quote-fallback]');
    if (!fb) return;
    const to = this.contactEmail || '';
    const subject = encodeURIComponent('Custom case quote request');
    const body = encodeURIComponent(this._serializeSpec());
    const href = 'mailto:' + to + '?subject=' + subject + '&body=' + body;

    fb.replaceChildren();
    fb.append("We couldn't send your request automatically. ");
    const a = document.createElement('a');
    a.href = href;
    a.className = 'cased__quote-fallback-link';
    // Without a store contact email the mailto has no recipient, so an "email
    // directly" link would open an empty compose window. Label it as a copyable
    // draft instead so the pre-filled spec isn't silently lost (WR-04).
    a.textContent = to ? 'Email your spec directly' : 'Open a pre-filled email draft';
    fb.appendChild(a);
    fb.append(to
      ? " — we've pre-filled the details for you."
      : " — add our contact address and send; we've pre-filled the details for you.");
    fb.classList.add('is-visible');
    fb.removeAttribute('hidden');
  }

  // Revert the success state back to the editable form, RETAINING the visible
  // contact-field values so a tweaked design can be re-quoted (D-13).
  _resetQuoteForm() {
    this._quoteSent = false;
    if (!this.quoteForm) return;
    const success = this.quoteForm.querySelector('.cased__quote-success');
    if (success) success.classList.remove('is-visible');
    this.quoteForm.classList.remove('cased__quote-form--sent');
    const fb = this.quoteForm.querySelector('[data-quote-fallback]');
    if (fb) {
      fb.classList.remove('is-visible');
      fb.setAttribute('hidden', '');
    }
  }

  // CTA opens + scrolls to the quote step (D-11).
  _activateQuoteCta() {
    if (!this.quoteStep) return;
    this.quoteStep.open = true;
    if (typeof this.quoteStep.scrollIntoView === 'function') {
      this.quoteStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Shopify's {% form 'contact' %} prepends hidden inputs (authenticity_token,
    // form_type, utf8) — skip them so focus lands on the first real field (WR-01).
    const firstField = this.quoteForm && this.quoteForm.querySelector('input:not([type="hidden"]), select, textarea');
    if (firstField && typeof firstField.focus === 'function') {
      // Defer focus so the smooth scroll isn't cut short.
      setTimeout(() => firstField.focus(), 300);
    }
  }

  // Stage toolbar (ENGINE-06, Plan 05). Each button drives an engine method that
  // acts on the existing OrbitControls/scene — no new pointer math. The 4 toggle
  // tools (auto-rotate, X-ray, measurements, lights) flip a boolean, update
  // aria-pressed + the active-visual class, and reflect live state. X-ray writes
  // the SAME engine boolean (this.engine._xray) the partition-step auto-engage uses
  // (D-07, Pitfall 4) — no separate flag. All engine calls are guarded so a dead
  // engine no-ops (the toolbar is hidden in that case anyway).
  _handleToolbarClick(event) {
    const btn = event.target.closest ? event.target.closest('[data-tool]') : null;
    if (!btn) return;
    event.preventDefault();
    const tool = btn.dataset.tool;
    const eng = this.engine;

    switch (tool) {
      case 'zoom-in':
        if (eng) eng.zoomStep(1 / 1.2);
        return;
      case 'zoom-out':
        if (eng) eng.zoomStep(1.2);
        return;
      case 'reset':
        if (eng) eng.resetView();
        return;
      case 'autorotate': {
        const next = !this._toolState.autorotate;
        this._toolState.autorotate = next;
        if (eng) eng.setAutoRotate(next);
        this._setToolPressed(btn, next);
        return;
      }
      case 'xray': {
        // Read/write the SAME source of truth the partition step uses. If the
        // engine is live it owns this._xray; keep the local mirror in sync.
        const next = eng ? !eng._xray : !this._toolState.xray;
        this._toolState.xray = next;
        if (eng) eng.setXray(next);
        this._setToolPressed(btn, next);
        return;
      }
      case 'measure': {
        const next = !this._toolState.measure;
        this._toolState.measure = next;
        if (eng) eng.toggleMeasurements(next);
        this._setToolPressed(btn, next);
        return;
      }
      case 'lights': {
        const next = !this._toolState.lights;
        this._toolState.lights = next;
        if (eng) eng.setLights(next);
        this._setToolPressed(btn, next);
        return;
      }
      default:
        return;
    }
  }

  // Keep a toggle button's aria-pressed + active-visual (foreground inset ring via
  // .is-selected) in sync with the live tool state.
  _setToolPressed(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-selected', !!on);
  }

  // Reflect the engine's live X-ray boolean onto the toolbar X-ray button. Called
  // after the partition step auto-engages/restores X-ray so the toolbar toggle never
  // desyncs from the shared this.engine._xray source of truth (D-07, Pitfall 4).
  _syncXrayToolbar() {
    if (!this.toolbar) return;
    const btn = this.toolbar.querySelector('[data-tool="xray"]');
    if (!btn) return;
    const on = this.engine ? !!this.engine._xray : this._toolState.xray;
    this._toolState.xray = on;
    this._setToolPressed(btn, on);
  }

  // Download the current 3D view as a PNG (SHARE-03). No-op with the engine dead.
  _downloadSnapshot() {
    if (!this.engine) return;
    const a = document.createElement('a');
    a.href = this.engine.snapshot();
    a.download = 'caselabs-design.png';
    a.click();
  }

  _handleDimChange(event) {
    const input = event.target;
    const dim = input.dataset.dim;
    if (!dim) return;

    if (this._quoteSent) this._resetQuoteForm();
    const minimums = (this._resolved || this._resolvePhysicalState()).minimumRequested;
    const min = minimums[dim];
    const max = this.limits.maxDim;
    const raw = parseInt(input.value, 10);
    const val = Math.max(min, Math.min(max, isNaN(raw) ? this.state[dim] : raw));
    this.state[dim] = val;
    this._resolvePhysicalState();

    // Keep the paired range + number inputs in sync.
    this._boundInputs.forEach((sibling) => {
      if (sibling !== input && sibling.dataset.dim === dim) {
        sibling.value = String(val);
      }
    });

    this._updateSummaries();
    this._updateDesignSummary();
    this._updateEstimate();
    this._updateItemFit();
    this._rebuild();
    this._persist();
  }

  _observeStageForLazyBoot() {
    if (!this.stage) return;
    this._io = new IntersectionObserver(
      async (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this._io = null;
        if (!this._webglOK()) {
          this._showStaticFallback();
          return;
        }
        try {
          this._syncEngineLabels();
          // Import via the version-hashed asset_url from the section (CDN cache
          // bust). The bare relative path is only a fallback — it has no ?v=
          // param, so the CDN can serve a stale engine after a theme push.
          const engineSrc = this.dataset.engineSrc || './case-designer-engine.js';
          const { CaseEngine } = await import(engineSrc);
          this.engine = new CaseEngine(this.canvas, this.state, {
            onFatal: () => this._showStaticFallback(),
            onHardwareDegraded: (status) => this._reflectHardwareStatus(status),
            hardwareSrc: this.dataset.hardwareSrc,
          });
          // Deterministic capture hook (Phase 5 render-fidelity harness).
          // Created ONLY when the page URL carries ?harness=1 so it is absent
          // from every production page (threat T-05-01: no debug hook shipped
          // live). Every closure delegates behind the existing `if (this.engine)`
          // guard idiom so a dead/booting engine simply no-ops (ENGINE-03).
          if (new URLSearchParams(location.search).has('harness')) {
            window.__CASE_HARNESS__ = {
              setState: (s) => {
                if (this.engine) this.engine.updateConfig(s);
              },
              setBackdrop: (c) => {
                if (this.engine) this.engine.setBackdrop(c);
              },
              setPose: (p) => {
                if (this.engine) this.engine.setPose(p);
              },
              renderOnce: (o) => {
                if (this.engine) this.engine.renderOnce(o);
              },
              snapshot: () => (this.engine ? this.engine.snapshot() : null),
              manifest: () => (this.engine ? this.engine.getLayoutManifest() : null),
              visibility: (components) => (this.engine ? this.engine.measureHardwareVisibility(components) : {}),
              // Engine MODES. Without this the deployed fold-out table was
              // unreachable from the harness (setTablePreview is not a state key
              // and updateConfig never touches _tablePreview), so no pose could
              // ever capture it and it went entirely unverified.
              setMode: (m) => {
                if (!this.engine || !m) return;
                if ('tablePreview' in m) this.engine.setTablePreview(!!m.tablePreview);
              },
              geometry: (o) => (this.engine ? this.engine.probeGeometry(o) : {}),
              ready: () => !!this.engine && this.engine.isReady(),
            };
          }
          // The engine boots long after the state settles (lazy init on scroll),
          // so apply the table pose now: a `?s=frontlid&tb=right` deep link, or a
          // restored session, must open already deployed rather than waiting for
          // the customer to re-pick a side.
          this._syncTablePreview();
          this.engine.onFirstFrame(() => {
            if (this.canvas) this.canvas.classList.add('is-visible');
            // The canvas is live — the snapshot has something to capture (SHARE-03).
            if (this.snapshotBtn) this.snapshotBtn.removeAttribute('hidden');
            // The engine is live — reveal the stage toolbar (only affects the 3D).
            if (this.toolbar) this.toolbar.removeAttribute('hidden');
          });
        } catch (e) {
          this._showStaticFallback();
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this._io.observe(this.stage);
  }

  _showStaticFallback() {
    if (this.canvas) this.canvas.classList.remove('is-visible');
    if (this.note) this.note.classList.add('is-visible');
    // No live canvas → nothing to snapshot; hide the download control.
    if (this.snapshotBtn) this.snapshotBtn.setAttribute('hidden', '');
    // No live canvas → the toolbar has nothing to act on; hide it (ENGINE-03).
    // Sidebar controls (styles, partitions, item-fit) stay fully live.
    if (this.toolbar) this.toolbar.setAttribute('hidden', '');
  }

  // Reflect a degraded hardware render onto the element so it is observable from
  // OUTSIDE the engine — from the console during a support call, from a test, and by
  // any future notice that tells the customer the view is simplified. Distinct from
  // _showStaticFallback: the canvas is alive and correct, it is the hardware that is
  // standing in. Leaving this state invisible is what let a stale theme serve
  // placeholder geometry on a customer-facing page without anyone noticing.
  _reflectHardwareStatus(status) {
    this.hardwareStatus = status;
    if (status && status.degraded) this.dataset.hardwareDegraded = 'true';
    else delete this.dataset.hardwareDegraded;
  }

  _webglOK() {
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch (e) {
      return false;
    }
  }

  _uid() {
    this._uidCounter = (this._uidCounter || 0) + 1;
    return this._uidCounter;
  }
}

customElements.define('case-designer', CaseDesigner);
